import { ApiError } from "./api";

/** La cuenta de un jugador solo la toca quien lo dio de alta (Opción A,
 * Eduardo 2026-09-30).
 *
 * Tener a un jugador inscrito en un torneo propio sirve para todo lo del
 * torneo, pero no para su cuenta: «Generar PIN», «Ver como jugador»,
 * «Reactivar cuenta», «Acceso propio», su correo, «Fusionar» y «Eliminar» son
 * de la organización que lo registró (o del soporte de Torneos123). El backend
 * manda en cada jugador:
 *
 * - `can_manage_account`: con `false` la pantalla apaga esos botones y explica
 *   por qué en un aviso fijo, en vez de fallar al pulsarlos;
 * - `account_has_owner`: si el jugador es de ALGUNA organización. Con `false`
 *   no es de ninguna (lo cargó soporte, es un registro antiguo o su torneo no
 *   tiene organizador titular) y solo lo atiende soporte: decirle al admin «lo
 *   registró otra organización» lo mandaba a buscar a alguien que no existe.
 *
 * Y `has_account` dice si el jugador ya tiene con qué entrar (cuenta ligada o
 * solo «Acceso propio», misma regla que el rechazo del backend): «Olvidé mi
 * contraseña» solo le sirve a quien la tiene. Sin ese dato (respuestas que no
 * lo traen), `user_id`. */

export const PLAYER_ACCOUNT_NOT_OWNED_CODE = "player_account_not_owned";

type OwnershipFields = {
  can_manage_account?: boolean | null;
  account_has_owner?: boolean | null;
  has_account?: boolean | null;
  user_id?: number | null;
};

/** Solo `false` apaga los botones. `undefined`/`null` (respuestas que no saben
 * quién mira, o un backend anterior) no apagan nada: el servidor decide. */
export function canManagePlayerAccount(player: OwnershipFields | null | undefined): boolean {
  return player?.can_manage_account !== false;
}

/** De quién es la cuenta que este admin no puede tocar. */
export interface AccountNotOwnedInfo {
  /** true = lo registró OTRA organización; false = no es de ninguna (solo soporte). */
  hasOwner: boolean;
  /** true = el jugador ya tiene con qué entrar (cuenta ligada o «Acceso propio»). */
  hasAccount: boolean;
}

/** Lo que el backend dijo del jugador. Sin el dato de dueño (backend anterior)
 * se asume el caso común: lo registró otra organización. Si tiene cuenta lo
 * dice `has_account` (también cuenta quien entra solo con «Acceso propio»,
 * que no tiene `user_id` en el perfil); sin ese dato, `user_id`. */
export function accountNotOwnedInfo(player: OwnershipFields | null | undefined): AccountNotOwnedInfo {
  const hasAccount =
    typeof player?.has_account === "boolean" ? player.has_account : player?.user_id !== null && player?.user_id !== undefined;
  return {
    hasOwner: player?.account_has_owner !== false,
    hasAccount,
  };
}

/** Para soporte (el superadministrador): un jugador cuya cuenta no es de
 * ninguna organización y que él puede asignar. */
export function isOwnerlessPlayer(player: OwnershipFields | null | undefined): boolean {
  return player?.account_has_owner === false;
}

export const ACCOUNT_OTHER_ORGANIZATION_LABEL = "Lo registró otra organización";
export const ACCOUNT_SUPPORT_ONLY_LABEL = "Cuenta a cargo de soporte";

/** Etiqueta corta para la fila de una lista. */
export function accountNotOwnedLabel(player: OwnershipFields | null | undefined): string {
  return accountNotOwnedInfo(player).hasOwner ? ACCOUNT_OTHER_ORGANIZATION_LABEL : ACCOUNT_SUPPORT_ONLY_LABEL;
}

const WHO_OTHER_ORGANIZATION = "la organización que lo registró o el soporte de Torneos123";
const ASK_OTHER_ORGANIZATION = "a la organización que lo registró o al soporte de Torneos123";
const WHO_SUPPORT = "el soporte de Torneos123";
const ASK_SUPPORT = "al soporte de Torneos123";

function accessTail(info: AccountNotOwnedInfo): string {
  if (info.hasAccount) return " Si olvidó su contraseña, que use «Olvidé mi contraseña» al entrar.";
  return (
    ` Todavía no tiene cuenta para entrar: pídele ${info.hasOwner ? ASK_OTHER_ORGANIZATION : ASK_SUPPORT} que se la ` +
    "cree («Olvidé mi contraseña» solo le sirve a quien ya tiene cuenta)."
  );
}

/** Explicación completa: qué sí puede hacer el admin, quién puede tocar la
 * cuenta y qué puede hacer el jugador. Mismo texto que el rechazo del backend. */
export function accountNotOwnedNote(info: AccountNotOwnedInfo): string {
  const tournamentPart = "En tu torneo puedes inscribirlo, programarlo, cobrarle y capturar sus resultados, pero su cuenta ";
  if (info.hasOwner) {
    return (
      `A este jugador no lo dio de alta tu organización. ${tournamentPart}(PIN, «Ver como jugador», «Acceso propio», ` +
      `su correo o eliminarlo) solo la puede tocar ${WHO_OTHER_ORGANIZATION}; fusionarlo con otro perfil, solo ` +
      `soporte.${accessTail(info)}`
    );
  }
  return (
    `La cuenta de este jugador no está asignada a ninguna organización. ${tournamentPart}(PIN, «Ver como jugador», ` +
    `«Acceso propio», su correo, fusionarlo o eliminarlo) solo la puede tocar ${WHO_SUPPORT}. Si es un jugador de tu ` +
    `organización, pídele a soporte que te lo asigne.${accessTail(info)}`
  );
}

/** El aviso de una lista: explica cada etiqueta que aparece en ella. Cadena
 * vacía si todos los jugadores son del admin. */
export function accountNotOwnedListNote(players: Array<OwnershipFields | null | undefined>): string {
  const foreign = players.filter((player) => !canManagePlayerAccount(player));
  if (foreign.length === 0) return "";
  const otherOrganization = foreign.some((player) => accountNotOwnedInfo(player).hasOwner);
  const supportOnly = foreign.some((player) => !accountNotOwnedInfo(player).hasOwner);
  const parts = [
    "En tus torneos puedes inscribir, programar, cobrar y capturar los resultados de todos estos jugadores, pero la " +
      "cuenta de los que traen una marca (PIN, «Ver como jugador», «Acceso propio», su correo, fusionarlos o " +
      "eliminarlos) no la puedes tocar tú.",
  ];
  if (otherOrganization) {
    parts.push(
      `«${ACCOUNT_OTHER_ORGANIZATION_LABEL}»: no los dio de alta tu organización; su cuenta la toca ` +
        "la organización que los registró o el soporte de Torneos123 (fusionarlos con otro perfil, solo soporte).",
    );
  }
  if (supportOnly) {
    parts.push(
      `«${ACCOUNT_SUPPORT_ONLY_LABEL}»: no están asignados a ninguna organización; su cuenta solo la toca el soporte ` +
        "de Torneos123. Si son jugadores de tu organización, pídele a soporte que te los asigne.",
    );
  }
  parts.push(
    "Si olvidaron su contraseña, que usen «Olvidé mi contraseña» al entrar; si todavía no tienen cuenta, hay que " +
      "pedírsela a quien toca su cuenta.",
  );
  return parts.join(" ");
}

/** Por qué el correo no se puede editar en la ficha de un jugador ajeno. */
export function accountNotOwnedEmailNote(info: AccountNotOwnedInfo): string {
  const who = info.hasOwner ? "la organización que registró al jugador o el soporte de Torneos123" : WHO_SUPPORT;
  return `El correo solo lo cambia ${who}: a ese correo llegan sus datos de acceso.`;
}

/** Campos del perfil que tocan la CUENTA del jugador: a su correo llegan sus
 * datos de acceso, así que solo los edita su dueño (el backend rechaza el
 * cambio con `player_email_not_owned`). Una pantalla que edita el perfil
 * (Jugadores, «Editar registro» en Inscripciones) bloquea estos campos y no
 * los manda, para que lo demás de la edición sí se guarde. */
export function isAccountLockedProfileField(fieldKey: string, player: OwnershipFields | null | undefined): boolean {
  return fieldKey === "email" && !canManagePlayerAccount(player);
}

/** «Fusionar»: el candidato es de otra organización (o de ninguna). Juntar dos
 * perfiles pide ser dueño de los dos, así que entre organizaciones solo lo
 * hace soporte: se dice, en vez de dejar al admin sin salida. */
export function accountNotOwnedMergeNote(info: AccountNotOwnedInfo): string {
  const label = info.hasOwner ? ACCOUNT_OTHER_ORGANIZATION_LABEL : ACCOUNT_SUPPORT_ONLY_LABEL;
  return `${label}: para juntar estos dos perfiles pídeselo al soporte de Torneos123.`;
}

/** Bienvenida enviada por quien no dio de alta al jugador
 * (`credentials_reason: "not_account_owner"`): salió sin usuario ni
 * contraseña. Si además el jugador no tiene correo (`emailStatus: "no_email"`)
 * no salió NADA por correo: solo quedó el comunicado dentro de la app. */
export function welcomeNotAccountOwnerMessage(
  playerName: string,
  info: AccountNotOwnedInfo,
  emailStatus?: string | null,
): string {
  const who = info.hasOwner ? "quien lo registró o el soporte de Torneos123" : WHO_SUPPORT;
  const reason = info.hasOwner
    ? "no lo dio de alta tu organización"
    : "su cuenta no está asignada a ninguna organización";
  if (emailStatus === "no_email") {
    return (
      `Bienvenida registrada solo dentro de la app: ${playerName} no tiene correo registrado, así que no le llegó ` +
      `nada por correo. Como ${reason}, su correo y sus datos de acceso solo los puede poner ${who}.`
    );
  }
  const tail = info.hasAccount
    ? " Si ya tiene cuenta, entra con la suya; si olvidó su contraseña, que use «Olvidé mi contraseña» al entrar."
    : ` Todavía no tiene cuenta para entrar: pídele ${info.hasOwner ? ASK_OTHER_ORGANIZATION : ASK_SUPPORT} que se la cree.`;
  return (
    `Bienvenida enviada a ${playerName} sin usuario ni contraseña: ${reason}, y sus datos de acceso solo los puede ` +
    `dar ${who}.${tail}`
  );
}

/** ¿El error es el rechazo del backend por no ser dueño de la cuenta? */
export function isAccountNotOwnedError(err: unknown): boolean {
  return err instanceof ApiError && err.code === PLAYER_ACCOUNT_NOT_OWNED_CODE;
}
